import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  CalendarIcon,
  Users,
  BookOpen,
  CheckCircle,
  Clock,
  TrendingUp,
  BarChart3,
  UserPlus,
  Award,
  Target,
  Activity,
  Calendar as CalendarDays,
  ArrowUp,
  ArrowDown,
  Minus,
  FileText,
  RefreshCw,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { analyticsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";
import ModernPageTemplate from "@/components/common/ModernPageTemplate";

const Dashboard = () => {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date()
  });
  const [isLoading, setIsLoading] = useState(true);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [dashboardData, setDashboardData] = useState({
    userStats: {},
    companyStats: {},
    systemStats: {}
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const params = {
        from: dateRange?.from ? format(dateRange.from, 'yyyy-MM-dd') : undefined,
        to: dateRange?.to ? format(dateRange.to, 'yyyy-MM-dd') : undefined
      };

      // Fetch all dashboard sections in parallel for faster loading
      const [
        userStatsResponse,
        companyStatsResponse,
        systemStatsResponse
      ] = await Promise.all([
        analyticsAPI.getUserStats(params).catch(err => ({ data: { success: false, data: {} } })),
        analyticsAPI.getCompanyStats(params).catch(err => ({ data: { success: false, data: {} } })),
        analyticsAPI.getSystemStats(params).catch(err => ({ data: { success: false, data: {} } }))
      ]);

      setDashboardData({
        userStats: userStatsResponse.data.success ? userStatsResponse.data.data : {},
        companyStats: companyStatsResponse.data.success ? companyStatsResponse.data.data : {},
        systemStats: systemStatsResponse.data.success ? systemStatsResponse.data.data : {}
      });

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      toast({
        title: "Error",
        description: "Failed to load dashboard data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const handleDateRangeSelect = (range) => {
    setDateRange(range);
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    description,
    color = "blue",
    trend,
    comparison,
    progress,
  }) => (
    <Card
      className={`relative overflow-hidden border-l-4 border-l-${color}-500 bg-gradient-to-br from-${color}-50 to-white hover:shadow-lg transition-all duration-300`}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
        <CardTitle className={`text-sm font-medium text-${color}-700`}>
          {title}
        </CardTitle>
        <div className={`p-2 bg-${color}-100 rounded-lg`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
      </CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold text-${color}-900 mb-2`}>
          {value}
        </div>

        {progress !== undefined && (
          <div className="mb-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-gray-600">Progress</span>
              <span className={`text-${color}-600 font-medium`}>
                {progress}%
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}

        {description && (
          <p className="text-xs text-gray-600 mb-2">{description}</p>
        )}

        {trend && (
          <div className="flex items-center gap-2">
            {trend.difference > 0 ? (
              <ArrowUp className="h-3 w-3 text-green-500" />
            ) : trend.difference < 0 ? (
              <ArrowDown className="h-3 w-3 text-red-500" />
            ) : (
              <Minus className="h-3 w-3 text-gray-400" />
            )}
            <span
              className={`text-xs font-medium ${
                trend.difference > 0
                  ? "text-green-600"
                  : trend.difference < 0
                  ? "text-red-600"
                  : "text-gray-500"
              }`}
            >
              {Math.abs(trend.difference)}% vs company avg
            </span>
          </div>
        )}

        {comparison && (
          <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
            <span className="text-gray-600">Company: </span>
            <span className="font-medium text-gray-800">{comparison}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const ActivityItem = ({ activity, color }) => {
    const getIcon = (type) => {
      switch (type) {
        case "exam_completed":
          return CheckCircle;
        case "candidate_added":
          return UserPlus;
        default:
          return Activity;
      }
    };

    const IconComponent = getIcon(activity.type);

    return (
      <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
        <div className={`p-2 bg-${color}-100 rounded-full flex-shrink-0`}>
          <IconComponent className={`h-4 w-4 text-${color}-600`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-gray-900 truncate">
              {activity.title}
            </p>
            {activity.score && (
              <Badge
                variant="outline"
                className={`text-${color}-600 border-${color}-200`}
              >
                {activity.score}%
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-600 truncate">
            {activity.candidateName}
            {activity.examName && ` • ${activity.examName}`}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {format(new Date(activity.date), "MMM dd, HH:mm")}
          </p>
        </div>
      </div>
    );
  };

  const UpcomingExamItem = ({ exam, color }) => (
    <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
      <div className={`p-2 bg-${color}-100 rounded-full`}>
        <CalendarDays className={`h-4 w-4 text-${color}-600`} />
      </div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">
          {exam.candidateName}
        </p>
        <p className="text-sm text-gray-600">{exam.examName}</p>
        <p className="text-xs text-gray-500">
          {format(new Date(exam.date), "MMM dd, yyyy 'at' HH:mm")}
        </p>
      </div>
      <Badge variant="secondary">{exam.duration || 0}min</Badge>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="text-center">
          <p className="text-gray-600">No data available</p>
          <Button onClick={fetchDashboardData} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const { userStats, companyStats, systemStats } = dashboardData;

  // Chart colors
  const chartColors = {
    user: "#3b82f6",
    company: "#8b5cf6",
    success: "#10b981",
    warning: "#f59e0b",
    danger: "#ef4444",
  };

  const handleRefresh = async () => {
    await fetchDashboardData();
    toast({
      title: "Refreshed",
      description: "Dashboard data has been refreshed successfully",
    });
  };

  const handleExport = () => {
    toast({
      title: "Export",
      description: "Export functionality coming soon",
    });
  };

  return (
    <ModernPageTemplate
      title="Analytics Dashboard"
      subtitle="Comprehensive overview of your performance vs company metrics"
      contentClassName="dashboard-page"
      onRefresh={handleRefresh}
      customActions={
        <>
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="admin-action-btn"
                title="Select Date Range"
              >
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={windowWidth < 768 ? 1 : 2}
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
          
          <Button 
            onClick={handleExport}
            variant="outline"
            size="sm"
            className="admin-action-btn"
            title="Export Data"
          >
            <FileText className="h-4 w-4" />
          </Button>
        </>
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center h-[calc(100vh-300px)]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      ) : !dashboardData ? (
        <div className="flex items-center justify-center h-[calc(100vh-300px)]">
          <div className="text-center">
            <p className="text-gray-600">No data available</p>
            <Button onClick={fetchDashboardData} className="mt-4">
              Retry
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-6">{/* System Overview */}
          {systemStats && (
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-700 flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  System Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                    <BookOpen className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {systemStats.totalActiveExams || 0}
                    </div>
                    <p className="text-sm text-gray-600">Active Exams</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                    <Users className="h-6 w-6 text-green-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {systemStats.totalQuestions || 0}
                    </div>
                    <p className="text-sm text-gray-600">Total Questions</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <Award className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">
                      {systemStats.categoryPerformance?.length || 0}
                    </div>
                    <p className="text-sm text-gray-600">Categories</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Your Performance */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Target className="h-5 w-5 text-blue-600" />
              Your Performance
            </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Your Candidates"
            value={userStats.totalCandidates || 0}
            icon={Users}
            description="Candidates you created"
            color="blue"
            trend={{
              difference:
                userStats.totalCandidates -
                companyStats.totalCandidates +
                userStats.totalCandidates,
            }}
            comparison={`${companyStats.totalCandidates || 0} total`}
          />
          <StatCard
            title="Completed Exams"
            value={userStats.completedExams || 0}
            icon={CheckCircle}
            description="Exams completed by your candidates"
            color="green"
            progress={userStats.completionRate || 0}
            comparison={`${companyStats.completedExams || 0} company`}
          />
          <StatCard
            title="Pending Exams"
            value={userStats.pendingExams || 0}
            icon={Clock}
            description="Awaiting completion"
            color="orange"
            comparison={`${companyStats.pendingExams || 0} company`}
          />
          <StatCard
            title="Average Score"
            value={`${userStats.avgScore || 0}%`}
            icon={TrendingUp}
            description="Your candidates' average"
            color="purple"
            trend={{
              difference:
                (userStats.avgScore || 0) - (companyStats.avgScore || 0),
            }}
            comparison={`${companyStats.avgScore || 0}% company`}
          />
        </div>
      </div>

          {/* Company Overview */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-purple-600" />
              Company Overview
            </h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          <StatCard
            title="Total Candidates"
            value={companyStats.totalCandidates || 0}
            icon={Users}
            description="All company candidates"
            color="purple"
          />
          <StatCard
            title="Company Completed"
            value={companyStats.completedExams || 0}
            icon={CheckCircle}
            description="All completed exams"
            color="green"
            progress={companyStats.completionRate || 0}
          />
          <StatCard
            title="Company Pending"
            value={companyStats.pendingExams || 0}
            icon={Clock}
            description="All pending exams"
            color="orange"
          />
          <StatCard
            title="Company Average"
            value={`${companyStats.avgScore || 0}%`}
            icon={BarChart3}
            description="Overall company performance"
            color="purple"
          />
        </div>
      </div>

          {/* Charts Section */}
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Trends Chart */}
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">
                  Performance Trends
                </CardTitle>
                <CardDescription className="text-sm">
                  Your performance vs company trends over time
                </CardDescription>
              </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart>
                  <defs>
                    <linearGradient
                      id="userGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.user}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.user}
                        stopOpacity={0}
                      />
                    </linearGradient>
                    <linearGradient
                      id="companyGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={chartColors.company}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={chartColors.company}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(value) => format(new Date(value), "MMM dd")}
                    stroke="#666"
                  />
                  <YAxis stroke="#666" />
                  <Tooltip
                    labelFormatter={(value) => format(new Date(value), "PPP")}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="candidates"
                    data={userStats.trends || []}
                    stroke={chartColors.user}
                    fillOpacity={1}
                    fill="url(#userGradient)"
                    name="Your Candidates"
                  />
                  <Area
                    type="monotone"
                    dataKey="candidates"
                    data={companyStats.trends || []}
                    stroke={chartColors.company}
                    fillOpacity={1}
                    fill="url(#companyGradient)"
                    name="Company Candidates"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

            {/* Score Comparison */}
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-gray-800">
                  Score Comparison
                </CardTitle>
                <CardDescription className="text-sm">Average performance comparison</CardDescription>
              </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[
                    {
                      name: "Your Avg",
                      score: userStats.avgScore || 0,
                      fill: chartColors.user,
                    },
                    {
                      name: "Company Avg",
                      score: companyStats.avgScore || 0,
                      fill: chartColors.company,
                    },
                  ]}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#666" />
                  <YAxis domain={[0, 100]} stroke="#666" />
                  <Tooltip
                    formatter={(value) => [`${value}%`, "Score"]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="score"
                    radius={[4, 4, 0, 0]}
                    fill="url(#colorGradient)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

          {/* Recent Activities and Upcoming Exams */}
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Your Recent Activity */}
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-blue-700 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Your Recent Activity
                </CardTitle>
                <CardDescription className="text-sm">
                  Latest activities from your candidates
                </CardDescription>
              </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            <div className="space-y-2">
              {userStats.recentActivity?.length > 0 ? (
                userStats.recentActivity.map((activity, index) => (
                  <ActivityItem
                    key={activity.id || index}
                    activity={activity}
                    color="blue"
                  />
                ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Activity className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No recent activity</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Company Recent Activity */}
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-purple-700 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" />
                  Company Activity
                </CardTitle>
                <CardDescription className="text-sm">
                  Latest activities across the company
                </CardDescription>
              </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            <div className="space-y-2">
              {companyStats.recentActivity?.length > 0 ? (
                companyStats.recentActivity
                  .slice(0, 8)
                  .map((activity, index) => (
                    <ActivityItem
                      key={activity.id || index}
                      activity={activity}
                      color="purple"
                    />
                  ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No recent activity</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Upcoming Exams */}
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-green-700 flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Upcoming Exams
                </CardTitle>
                <CardDescription className="text-sm">Next 7 days schedule</CardDescription>
              </CardHeader>
          <CardContent className="max-h-96 overflow-y-auto">
            <div className="space-y-3">
              {userStats.upcomingExams?.length > 0 ? (
                userStats.upcomingExams.map((exam, index) => (
                  <UpcomingExamItem
                    key={exam.id || index}
                    exam={exam}
                    color="green"
                  />
                ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CalendarDays className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p className="text-sm">No upcoming exams</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </ModernPageTemplate>
  );
};

export default Dashboard;
